import { ChangeEvent, FormEvent, useContext, useEffect, useRef, useState } from "react";
import { CircularProgress, Dialog, DialogContent, TextField } from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { DialogTitle, FooterActions } from "components/Dialog";
import ConfigurationsContext from "context/configurations/ConfigurationsContext.ts";
import { useLazyGetAccountsByIdQuery } from "features/api/accountApi.ts";
import { useLazyFilterQuery, useLazyGetByIdQuery } from "features/api/orderApi.ts";
import { useGetUserDetailsQuery } from "features/api/userApi.ts";
import { OrderType, UserRole } from "types/declarations/enums.ts";
import { models } from "types/api/viewModels.ts";
import { GetOrderByIdResponseData } from "types/api/orders/getOrderById.ts";
import { RootState } from "stores/application.store.tsx";
import { DialogBody, DialogError, OrderChoice } from "../Dashboard.styles";
import { OrderLookupMode } from "../Dashboard.types";

const noOrderFoundMessage = "No order found. Check the order number and try again.";
const incompleteSearchMessage = "We couldn't complete this order search. Refine the order number and try again.";
const generalErrorMessage = "We couldn't check this order right now. Please try again.";

interface OrderLookupDialogProps {
	mode: OrderLookupMode | null;
	onClose: () => void;
}

const getProductLabel = (order: models["OrderViewModel"]) =>
	order.configurations?.[0]?.globals?.productLine?.description ??
	order.configurations?.[0]?.globals?.brand?.description ??
	"Product unavailable";

const getErrorStatus = (error: unknown) => {
	if (!error || typeof error !== "object" || !("status" in error)) return undefined;
	return Number((error as { status?: string | number }).status);
};

const orderIsEligible = (
	mode: OrderLookupMode,
	details: NonNullable<GetOrderByIdResponseData>,
	hasActiveAccount: boolean,
	createOrdersEnabled: boolean
) => {
	const isMerchOrder = details.orderType === OrderType.MERCH;
	if (mode === "replacement") {
		return !isMerchOrder && details.isReplacementEligible === true && hasActiveAccount;
	}
	return (
		!isMerchOrder &&
		details.orderType !== OrderType.CAMPAIGN &&
		details.orderType !== OrderType.CANCELLED &&
		details.isAddOnEligible === true &&
		createOrdersEnabled
	);
};

export const OrderLookupDialog = ({ mode, onClose }: OrderLookupDialogProps) => {
	const navigate = useNavigate();
	const { configurationsData } = useContext(ConfigurationsContext);
	const impersonation = useSelector((state: RootState) => state.impersonation.impersonationUser);
	const { data: userDetails } = useGetUserDetailsQuery();
	const [searchOrders] = useLazyFilterQuery();
	const [getOrderDetails] = useLazyGetByIdQuery();
	const [getAccounts] = useLazyGetAccountsByIdQuery();
	const [orderNumber, setOrderNumber] = useState("");
	const [matches, setMatches] = useState<models["OrderViewModel"][]>([]);
	const [error, setError] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const requestVersion = useRef(0);
	const submittingRef = useRef(false);
	const emulationKey = `${impersonation?.email ?? ""}:${impersonation?.accountId ?? ""}`;

	useEffect(() => {
		requestVersion.current += 1;
		submittingRef.current = false;
		setMatches([]);
		setError("");
		setIsSubmitting(false);
	}, [emulationKey]);

	const reset = () => {
		requestVersion.current += 1;
		setOrderNumber("");
		setMatches([]);
		setError("");
		setIsSubmitting(false);
		submittingRef.current = false;
	};

	const close = () => {
		reset();
		onClose();
	};

	const processOrder = async (order: models["OrderViewModel"], version: number) => {
		if (submittingRef.current) return;
		if (!order.orderId) {
			setError(noOrderFoundMessage);
			return;
		}

		submittingRef.current = true;
		setIsSubmitting(true);
		setError("");
		try {
			const [details, accounts] = await Promise.all([
				getOrderDetails(order.orderId, false).unwrap(),
				getAccounts(order.orderId, false).unwrap()
			]);
			if (requestVersion.current !== version || !mode || !details) return;

			const configurationAccounts =
				details.configurations?.map((configuration) => configuration.accountNumber) ?? [];
			const hasActiveAccount =
				userDetails?.role === UserRole.CARE ||
				(accounts ?? []).some(
					(account) => configurationAccounts.includes(account.accountNumber) && account.isActive === true
				);

			const isEligible = orderIsEligible(
				mode,
				details,
				hasActiveAccount,
				configurationsData?.featureFlags?.createOrdersEnabled === true
			);

			if (!isEligible) {
				setError(
					mode === "replacement"
						? "This order is not eligible for replacement parts."
						: "This order is not eligible for add-ons."
				);
				return;
			}

			close();
			navigate(`/${mode === "replacement" ? "replacements" : "add-ons"}/${order.orderId}`);
		} catch (requestError) {
			if (requestVersion.current !== version) return;
			const status = getErrorStatus(requestError);
			setError(status === 403 || status === 404 ? noOrderFoundMessage : generalErrorMessage);
		} finally {
			if (requestVersion.current === version) {
				submittingRef.current = false;
				setIsSubmitting(false);
			}
		}
	};

	const submit = async (event?: FormEvent) => {
		event?.preventDefault();
		if (submittingRef.current) return;
		const normalizedOrderNumber = orderNumber.trim();
		if (!normalizedOrderNumber) {
			setError("Original Order Number is required.");
			return;
		}

		const version = ++requestVersion.current;
		submittingRef.current = true;
		setIsSubmitting(true);
		setMatches([]);
		setError("");
		try {
			const result = await searchOrders(
				{ searchDates: { fromDate: undefined, toDate: undefined }, searchTerm: normalizedOrderNumber },
				false
			).unwrap();
			if (requestVersion.current !== version) return;
			const exactMatches = (result?.items ?? []).filter(
				(order) => order.orderNumber?.trim().toLowerCase() === normalizedOrderNumber.toLowerCase()
			);

			if (exactMatches.length === 0) {
				setError(result?.isPartialDataset ? incompleteSearchMessage : noOrderFoundMessage);
				return;
			}
			if (exactMatches.length > 1) {
				setMatches(exactMatches);
				return;
			}
			submittingRef.current = false;
			await processOrder(exactMatches[0]!, version);
		} catch (requestError) {
			if (requestVersion.current !== version) return;
			const status = getErrorStatus(requestError);
			setError(status === 403 || status === 404 ? noOrderFoundMessage : generalErrorMessage);
		} finally {
			if (requestVersion.current === version) {
				submittingRef.current = false;
				setIsSubmitting(false);
			}
		}
	};

	const handleOrderNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
		requestVersion.current += 1;
		setOrderNumber(event.target.value);
		setMatches([]);
		setError("");
		setIsSubmitting(false);
		submittingRef.current = false;
	};

	const title = mode === "replacement" ? "Order Replacements" : "Order Add-Ons";
	return (
		<Dialog
			open={mode !== null}
			onClose={close}
			fullWidth
			maxWidth="sm"
			aria-labelledby="order-lookup-title"
		>
			<DialogTitle
				title={title}
				handleClose={close}
				isLoading={isSubmitting}
			/>
			<form onSubmit={submit}>
				<DialogContent>
					<DialogBody>
						<TextField
							label="Original Order Number"
							value={orderNumber}
							onChange={handleOrderNumberChange}
							required
							autoFocus
							inputProps={{ "aria-describedby": error ? "order-lookup-error" : undefined }}
						/>
						{error && <DialogError id="order-lookup-error">{error}</DialogError>}
						{isSubmitting && (
							<CircularProgress
								size={24}
								aria-label="Checking order"
							/>
						)}
						{matches.length > 1 && (
							<div aria-label="Matching orders">
								<p>Choose the matching account and product:</p>
								{matches.map((order) => (
									<OrderChoice
										key={order.orderId}
										onClick={() => processOrder(order, requestVersion.current)}
										disabled={isSubmitting}
									>
										<strong>{order.accountNumber ?? "Account unavailable"}</strong>
										<span>{getProductLabel(order)}</span>
									</OrderChoice>
								))}
							</div>
						)}
					</DialogBody>
				</DialogContent>
				<FooterActions
					handleClose={close}
					handleConfirm={() => submit()}
					cancelText="CANCEL"
					confirmText="CONTINUE"
				/>
			</form>
		</Dialog>
	);
};
